import { useState, useEffect, useRef } from "react";
import api from "../services/api";
import "./FileConvertor.css";

const Files = () => {
  // ===== STATE MANAGEMENT =====
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // ===== EFFECTS =====
  useEffect(() => {
    fetchFiles();
  }, []);

  // ===== API CALLS =====
  const fetchFiles = async () => {
    try {
      setLoading(true);
      const response = await api.get("/upload");
      setFiles(response.data.data.files);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch files");
    } finally {
      setLoading(false);
    }
  };

  // ===== EVENT HANDLERS =====
  const handleFileUpload = async (file) => {
    if (!file) return;

    setUploading(true);
    setError("");
    setSuccessMessage("");

    const formData = new FormData();
    formData.append("file", file);

    try {
      await api.post("/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setSuccessMessage("File uploaded successfully!");
      fetchFiles();

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (err) {
      setError(err.response?.data?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm("Are you sure you want to delete this file?")) {
      return;
    }

    try {
      await api.delete(`/upload/${fileId}`);
      setSuccessMessage("File deleted successfully!");
      fetchFiles();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete file");
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileUpload(droppedFile);
    }
  };

  // ===== UTILITY FUNCTIONS =====
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const getFileIcon = (fileType) => {
    switch (fileType?.toLowerCase()) {
      case "pdf":
        return "fa-file-pdf-o text-danger";
      case "docx":
      case "doc":
        return "fa-file-word-o text-primary";
      case "xlsx":
      case "xls":
        return "fa-file-excel-o text-success";
      case "pptx":
      case "ppt":
        return "fa-file-powerpoint-o text-warning";
      case "txt":
        return "fa-file-text-o text-secondary";
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return "fa-file-image-o text-info";
      default:
        return "fa-file-o text-muted";
    }
  };

  // ===== COMPUTED VALUES =====
  const totalStorageUsed = files.reduce((acc, file) => acc + file.size, 0);

  // ===== RENDER FUNCTIONS =====
  const renderHeader = () => (
    <div className="files-page-header files-fade-in">
      <h1 className="display-4 fw-bold mb-3 files-page-title">
        <i className="fa fa-folder-open me-3"></i>
        File Management
      </h1>
      <p className="lead text-muted" style={{ maxWidth: "700px" }}>
        Upload, organize, and manage your files with ease. Drag and drop to get started.
      </p>
    </div>
  );

  const renderAlerts = () => (
    <>
      {error && (
        <div className="files-alert-modern files-alert-danger mb-4">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <i className="fa fa-exclamation-circle fa-lg me-3"></i>
              <span className="fw-semibold">{error}</span>
            </div>
            <button
              type="button"
              className="btn-close"
              onClick={() => setError("")}
              aria-label="Close"
            ></button>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="files-alert-modern files-alert-success mb-4">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <i className="fa fa-check-circle fa-lg me-3"></i>
              <span className="fw-semibold">{successMessage}</span>
            </div>
            <button
              type="button"
              className="btn-close"
              onClick={() => setSuccessMessage("")}
              aria-label="Close"
            ></button>
          </div>
        </div>
      )}
    </>
  );

  const renderUploadSection = () => (
    <div className="files-card files-slide-in mb-4">
      <div className="files-card-header">
        <h5 className="mb-0 fw-bold d-flex align-items-center">
          <i className="fa fa-cloud-upload me-2" style={{ color: "#4f46e5" }}></i>
          Upload File
        </h5>
      </div>
      <div className="card-body p-4">
        <div
          className={`files-upload-zone ${isDragging ? "files-dragging" : ""}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="text-center">
            <i className="fa fa-cloud-upload fa-4x files-upload-icon mb-3" style={{ color: "#4f46e5" }}></i>
            <h5 className="fw-bold mb-2">Drag & Drop your file here</h5>
            <p className="text-muted mb-3">or click to browse your files</p>
            <div className="d-flex align-items-center justify-content-center gap-2">
              <span className="badge bg-light text-dark border">
                <i className="fa fa-file-pdf-o me-1"></i>PDF
              </span>
              <span className="badge bg-light text-dark border">
                <i className="fa fa-file-word-o me-1"></i>DOCX
              </span>
              <span className="badge bg-light text-dark border">
                <i className="fa fa-file-image-o me-1"></i>Images
              </span>
              <span className="badge bg-light text-dark border">
                <i className="fa fa-file-text-o me-1"></i>More
              </span>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              className="d-none"
              onChange={(e) => handleFileUpload(e.target.files[0])}
            />
            {uploading && (
              <div className="mt-4">
                <div className="files-spinner mx-auto"></div>
                <p className="text-muted mt-3 mb-0">Uploading your file...</p>
              </div>
            )}
          </div>
        </div>
        <div className="mt-3 text-center">
          <small className="text-muted">
            <i className="fa fa-info-circle me-1"></i>
            Supported formats: PDF, DOCX, DOC, XLSX, Images, and more
          </small>
        </div>
      </div>
    </div>
  );

  const renderFilesTable = () => (
    <div className="files-card files-slide-in">
      <div className="files-card-header">
        <div className="d-flex align-items-center justify-content-between">
          <h5 className="mb-0 fw-bold d-flex align-items-center">
            <i className="fa fa-files-o me-2" style={{ color: "#4f46e5" }}></i>
            Your Files
            <span className="badge rounded-pill ms-2" style={{ backgroundColor: "#4f46e5", color: "white" }}>
              {files.length}
            </span>
          </h5>
          {files.length > 0 && (
            <button
              className="btn btn-sm btn-outline-primary rounded-pill"
              onClick={fetchFiles}
              title="Refresh files"
            >
              <i className="fa fa-refresh me-1"></i>
              Refresh
            </button>
          )}
        </div>
      </div>
      <div className="card-body p-0">
        {files.length === 0 ? (
          <div className="files-empty-state">
            <i className="fa fa-inbox fa-5x files-empty-icon d-block"></i>
            <h5 className="text-muted fw-bold mb-2">No files uploaded yet</h5>
            <p className="text-muted mb-0">
              Upload your first file using the upload area above
            </p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="files-table-modern">
              <thead>
                <tr>
                  <th className="files-file-icon-cell">
                    <i className="fa fa-file"></i>
                  </th>
                  <th>File Name</th>
                  <th>Type</th>
                  <th>Size</th>
                  <th>Upload Date</th>
                  <th style={{ width: "120px", textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {files.map((file) => (
                  <tr key={file._id}>
                    <td className="files-file-icon-cell">
                      <div className="files-file-icon-wrapper">
                        <i className={`fa ${getFileIcon(file.fileType)}`}></i>
                      </div>
                    </td>
                    <td>
                      <div className="fw-semibold text-dark">
                        {file.originalName}
                      </div>
                    </td>
                    <td>
                      <span className="files-badge-custom bg-light text-dark border">
                        {file.fileType?.toUpperCase() || "UNKNOWN"}
                      </span>
                    </td>
                    <td className="text-muted">{formatFileSize(file.size)}</td>
                    <td className="text-muted">
                      {new Date(file.uploadedAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </td>
                    <td className="text-center">
                      <button
                        className="btn btn-sm btn-danger files-action-btn rounded-pill px-3"
                        onClick={() => handleDelete(file._id)}
                        title="Delete file"
                      >
                        <i className="fa fa-trash me-1"></i>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderStorageInfo = () => {
    if (files.length === 0) return null;

    return (
      <div className="files-storage-card mt-4">
        <div className="d-flex align-items-center gap-4">
          <div className="files-storage-icon">
            <i className="fa fa-database fa-2x"></i>
          </div>
          <div className="flex-grow-1">
            <h6 className="fw-bold mb-1 text-dark">Storage Summary</h6>
            <p className="mb-0 text-muted">
              {files.length} file{files.length !== 1 ? "s" : ""} stored
            </p>
          </div>
          <div className="text-end">
            <div className="files-storage-text display-6 fw-bold mb-0">
              {formatFileSize(totalStorageUsed)}
            </div>
            <small className="text-muted">Total Size</small>
          </div>
        </div>
      </div>
    );
  };

  // ===== LOADING STATE =====
  if (loading) {
    return (
      <div className="files-main-wrapper">
        <div className="files-loading-wrapper">
          <div className="text-center">
            <div className="files-spinner mx-auto mb-3"></div>
            <p className="text-muted fw-semibold">Loading your files...</p>
          </div>
        </div>
      </div>
    );
  }

  // ===== MAIN RENDER =====
  return (
    <div className="files-main-wrapper">
      <div className="container py-5">
        {renderHeader()}
        {renderAlerts()}

        <div className="row g-4">
          <div className="col-12">
            {renderUploadSection()}
            {renderFilesTable()}
            {renderStorageInfo()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Files;
